<template>
  <div class="entitlements-container">
    <h3 class="section-title">Grant Access</h3>
    <div class="config-card">
      <p class="config-hint intro">
        Manually grant access without a Stripe payment: a single PPV item, a PPV package, or a
        premium (flex) subscription. Find the user, choose what to grant and for how long.
      </p>

      <!-- 1. User -->
      <div class="field">
        <label class="config-label">User</label>
        <div v-if="selectedUser" class="selected-user">
          <span>{{ selectedUser.email || selectedUser.username }}</span>
          <button class="btn-link" @click="clearUser">change</button>
        </div>
        <div v-else>
          <div class="user-search-row">
            <input
              v-model="userQuery"
              type="text"
              placeholder="Search by email..."
              class="text-input"
              @keyup.enter="searchUsers"
            />
            <button class="btn-secondary" @click="searchUsers">Search</button>
          </div>
          <ul v-if="userResults.length" class="user-results">
            <li v-for="u in userResults" :key="u.id" @click="selectUser(u)">
              {{ u.email || u.username }} <span class="muted">· {{ u.type }}</span>
            </li>
          </ul>
          <p v-else-if="userSearched" class="config-hint">No users found.</p>
        </div>
      </div>

      <template v-if="selectedUser">
        <!-- 2. Type -->
        <div class="field">
          <label class="config-label">What to grant</label>
          <div class="type-toggles">
            <label class="type-chip" :class="{ active: type === 'ppv' }" @click="type = 'ppv'">PPV item</label>
            <label class="type-chip" :class="{ active: type === 'ppv_package' }" @click="type = 'ppv_package'">PPV package</label>
            <label class="type-chip" :class="{ active: type === 'subscription' }" @click="type = 'subscription'">Subscription</label>
          </div>
        </div>

        <!-- 3a. PPV item picker -->
        <div v-if="type === 'ppv'" class="field">
          <label class="config-label">Media item</label>
          <div class="selected-user">
            <span v-if="selectedMedia">{{ selectedMedia.title }}</span>
            <span v-else class="muted">No item selected</span>
            <button class="btn-link" @click="showMediaPicker = true">{{ selectedMedia ? 'change' : 'select' }}</button>
          </div>
        </div>

        <!-- 3b. PPV package dropdown -->
        <div v-if="type === 'ppv_package'" class="field">
          <label class="config-label">Package</label>
          <select v-model="packageId" class="text-input">
            <option :value="null" disabled>Select a package...</option>
            <option v-for="p in packages" :key="p.id" :value="p.id">
              {{ p.name }}{{ p.is_active ? '' : ' (inactive)' }}
            </option>
          </select>
        </div>

        <!-- 4. Duration -->
        <div class="field">
          <label class="config-label">Access period</label>
          <div class="type-toggles">
            <label class="type-chip" :class="{ active: durationMode === 'preset' }" @click="durationMode = 'preset'">Duration</label>
            <label class="type-chip" :class="{ active: durationMode === 'custom' }" @click="durationMode = 'custom'">Until date</label>
            <label class="type-chip" :class="{ active: durationMode === 'unlimited' }" @click="durationMode = 'unlimited'">Unlimited</label>
          </div>

          <div v-if="durationMode === 'preset'" class="duration-row">
            <input type="number" min="1" v-model.number="presetValue" class="text-input duration-num"/>
            <select v-model="presetUnit" class="text-input duration-unit">
              <option value="days">days</option>
              <option value="weeks">weeks</option>
              <option value="months">months</option>
            </select>
            <span class="config-hint">Ends {{ previewEndLabel }}</span>
          </div>

          <div v-if="durationMode === 'custom'" class="duration-row">
            <input type="date" v-model="customEndDate" class="text-input"/>
          </div>

          <p v-if="durationMode === 'unlimited'" class="config-hint">Access never expires (until manually revoked).</p>
        </div>

        <div class="actions">
          <button class="btn-primary" :disabled="!canGrant || saving" @click="grant">
            {{ saving ? 'Granting...' : 'Grant access' }}
          </button>
        </div>
      </template>
    </div>

    <!-- Current access for the selected user -->
    <template v-if="selectedUser">
      <h3 class="section-title">Current access for {{ selectedUser.email || selectedUser.username }}</h3>
      <div class="config-card">
        <div class="current-row">
          <div class="current-label">Premium subscription</div>
          <div class="current-value">
            <span v-if="flexActive" class="badge active">Active{{ flexUntilLabel }}</span>
            <span v-else class="badge inactive">None</span>
            <button v-if="flexActive" class="btn-delete" @click="revokeSubscription">Revoke</button>
          </div>
        </div>

        <div v-if="entitlements.length" class="entitlement-list">
          <div v-for="e in entitlements" :key="e.id" class="entitlement-row">
            <div>
              <span class="badge type">{{ prettyType(e.type) }}</span>
              <span class="muted">{{ e.source }}</span>
              <span class="ent-asset">{{ e.asset_id }}</span>
            </div>
            <div class="entitlement-meta">
              <span class="config-hint">{{ formatRange(e) }}</span>
              <span :class="['badge', isActiveNow(e) ? 'active' : 'inactive']">{{ isActiveNow(e) ? 'Active' : 'Inactive' }}</span>
              <button v-if="e.active" class="btn-delete" @click="deactivateEntitlement(e)">Revoke</button>
            </div>
          </div>
        </div>
        <p v-else class="config-hint">No PPV / package entitlements.</p>
      </div>
    </template>

    <SelectItem
      v-model="showMediaPicker"
      title="Select media item"
      :lang="defaultLang"
      types="video,episode"
      @selected="onMediaSelected"
    />
  </div>
</template>

<script>
import SelectItem from '../components/SelectItem.vue'

export default {
  components: { SelectItem },

  data() {
    return {
      // user
      userQuery: '',
      userResults: [],
      userSearched: false,
      selectedUser: null,

      // grant form
      type: 'ppv',
      selectedMedia: null,
      showMediaPicker: false,
      packages: [],
      packageId: null,

      // duration
      durationMode: 'preset',
      presetValue: 7,
      presetUnit: 'days',
      customEndDate: '',

      // current access
      entitlements: [],
      flex: null,

      saving: false
    }
  },

  computed: {
    defaultLang() {
      return this.$config.defaultLanguage
    },

    flexActive() {
      // Active + not past its (optional) expiry.
      if (!this.flex || this.flex.active !== true) return false
      if (!this.flex.active_until) return true
      return new Date(this.flex.active_until) > new Date()
    },

    flexUntilLabel() {
      if (!this.flex || !this.flex.active_until) return ''
      return ' until ' + this.formatDate(this.flex.active_until)
    },

    computedEnd() {
      // Returns an ISO string (or null for unlimited / invalid).
      if (this.durationMode === 'unlimited') return null

      if (this.durationMode === 'custom') {
        if (!this.customEndDate) return null
        // End of the chosen day, in UTC.
        return new Date(this.customEndDate + 'T23:59:59Z').toISOString()
      }

      // preset
      var n = parseInt(this.presetValue, 10)
      if (!n || n < 1) return null
      var d = new Date()
      if (this.presetUnit === 'days') d.setDate(d.getDate() + n)
      else if (this.presetUnit === 'weeks') d.setDate(d.getDate() + n * 7)
      else if (this.presetUnit === 'months') d.setMonth(d.getMonth() + n)
      return d.toISOString()
    },

    previewEndLabel() {
      var end = this.computedEnd
      return end ? this.formatDate(end) : '—'
    },

    canGrant() {
      if (!this.selectedUser) return false
      if (this.durationMode === 'preset' && (!this.presetValue || this.presetValue < 1)) return false
      if (this.durationMode === 'custom' && !this.customEndDate) return false
      if (this.type === 'ppv') return Boolean(this.selectedMedia)
      if (this.type === 'ppv_package') return Boolean(this.packageId)
      return true // subscription
    }
  },

  methods: {
    // --- User ---
    searchUsers() {
      var q = (this.userQuery || '').trim()
      if (!q) return

      fetch('/admin/api/v2/users?match=' + encodeURIComponent(q))
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(resp => {
          this.userResults = resp.data || []
          this.userSearched = true
        })
        .catch(() => this.$root.app.showAlert('Error searching users', 'danger'))
    },

    selectUser(u) {
      this.selectedUser = u
      this.userResults = []
      this.userQuery = ''
      this.userSearched = false
      this.loadCurrentAccess()
    },

    clearUser() {
      this.selectedUser = null
      this.entitlements = []
      this.flex = null
    },

    // --- Options ---
    loadPackages() {
      fetch('/admin/api/v2/ppv-packages')
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(resp => { this.packages = resp.data || [] })
        .catch(() => {})
    },

    onMediaSelected(item) {
      this.selectedMedia = { id: item.id, title: item.meta.title }
    },

    // --- Current access ---
    loadCurrentAccess() {
      var uid = this.selectedUser.id

      fetch('/admin/api/v2/user-entitlements/user/' + uid)
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(resp => { this.entitlements = resp.data || [] })
        .catch(() => this.$root.app.showAlert('Error loading entitlements', 'danger'))

      fetch('/admin/api/v2/user-entitlements/subscription/' + uid)
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(resp => { this.flex = resp.data || null })
        .catch(() => { this.flex = null })
    },

    // --- Grant ---
    grant() {
      if (!this.canGrant) return
      this.saving = true

      if (this.type === 'subscription') {
        this.grantSubscription()
      } else {
        this.grantEntitlement()
      }
    },

    grantSubscription() {
      fetch('/admin/api/v2/user-entitlements/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { user_id: this.selectedUser.id, active_until: this.computedEnd } })
      })
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(() => {
          this.$root.app.showAlert('Subscription granted', 'success')
          this.afterGrant()
        })
        .catch(() => this.$root.app.showAlert('Error granting subscription', 'danger'))
        .finally(() => { this.saving = false })
    },

    grantEntitlement() {
      var assetId = this.type === 'ppv' ? this.selectedMedia.id : this.packageId

      fetch('/admin/api/v2/user-entitlements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            user_id: this.selectedUser.id,
            type: this.type,
            source: 'manual',
            asset_id: assetId,
            date_start: new Date().toISOString(),
            date_end: this.computedEnd,
            active: true,
            payment_status: 'paid'
          }
        })
      })
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(() => {
          this.$root.app.showAlert('Access granted', 'success')
          this.afterGrant()
        })
        .catch(() => this.$root.app.showAlert('Error granting access', 'danger'))
        .finally(() => { this.saving = false })
    },

    afterGrant() {
      this.selectedMedia = null
      this.packageId = null
      this.loadCurrentAccess()
    },

    // --- Revoke ---
    deactivateEntitlement(e) {
      fetch('/admin/api/v2/user-entitlements/' + e.id + '/deactivate', { method: 'POST' })
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(() => {
          this.$root.app.showAlert('Entitlement revoked', 'success')
          this.loadCurrentAccess()
        })
        .catch(() => this.$root.app.showAlert('Error revoking entitlement', 'danger'))
    },

    revokeSubscription() {
      fetch('/admin/api/v2/user-entitlements/subscription/' + this.selectedUser.id, { method: 'DELETE' })
        .then(resp => (resp.status === 200 ? resp.json() : Promise.reject(resp.statusText)))
        .then(() => {
          this.$root.app.showAlert('Subscription revoked', 'success')
          this.loadCurrentAccess()
        })
        .catch(() => this.$root.app.showAlert('Error revoking subscription', 'danger'))
    },

    // --- Helpers ---
    prettyType(t) {
      if (t === 'ppv') return 'PPV'
      if (t === 'ppv_package') return 'Package'
      if (t === 'subscription') return 'Subscription'
      return t
    },

    isActiveNow(e) {
      if (!e.active) return false
      var now = new Date()
      if (e.date_start && new Date(e.date_start) > now) return false
      if (e.date_end && new Date(e.date_end) <= now) return false
      return true
    },

    formatRange(e) {
      var start = e.date_start ? this.formatDate(e.date_start) : '—'
      var end = e.date_end ? this.formatDate(e.date_end) : 'unlimited'
      return start + ' → ' + end
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      var d = new Date(dateStr)
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    }
  },

  mounted() {
    this.$root.app.setActivePageTitle('Manual Entitlements', false)
    this.$root.$emit('set-toolbar', 'empty-toolbar')
    this.loadPackages()
  }
}
</script>

<style scoped>
.entitlements-container {
  padding: 1.5rem;
  max-width: 820px;
  margin: 0 auto;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

.config-card {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.intro {
  margin-top: 0;
  margin-bottom: 1.25rem;
}

.field {
  margin-bottom: 1.25rem;
}

.config-label {
  font-size: 0.85rem;
  color: #E2E8F0;
  margin-bottom: 0.5rem;
  display: block;
  font-weight: 600;
}

.config-hint {
  font-size: 0.75rem;
  color: #94A3B8;
  margin-top: 0.4rem;
}

.text-input {
  width: 100%;
  padding: 0.45rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.85rem;
  box-sizing: border-box;
}

.text-input:focus {
  outline: none;
  border-color: #20B4FF;
}

select.text-input option {
  background: #2a2e35;
  color: #fff;
}

.user-search-row {
  display: flex;
  gap: 0.5rem;
}

.user-results {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  overflow: hidden;
}

.user-results li {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: #E2E8F0;
}

.user-results li:hover {
  background: rgba(255, 255, 255, 0.06);
}

.selected-user {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  color: #fff;
}

.muted {
  color: #94A3B8;
  font-size: 0.8rem;
}

.type-toggles {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.type-chip {
  padding: 0.35rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #94A3B8;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  user-select: none;
}

.type-chip.active {
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  border-color: transparent;
  color: #fff;
}

.duration-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.6rem;
}

.duration-num {
  width: 80px;
}

.duration-unit {
  width: 110px;
}

.actions {
  margin-top: 1.5rem;
}

.btn-primary {
  padding: 0.5rem 1.25rem;
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 0.45rem 0.9rem;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  white-space: nowrap;
}

.btn-link {
  background: none;
  border: none;
  color: #20B4FF;
  cursor: pointer;
  font-size: 0.8rem;
  text-decoration: underline;
  padding: 0;
}

.btn-delete {
  background: transparent;
  color: #adb5bd;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0.2rem 0.5rem;
}

.btn-delete:hover {
  background: #dc3545;
  color: #fff;
}

.current-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 0.75rem;
}

.current-label {
  font-size: 0.9rem;
  color: #E2E8F0;
}

.current-value {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.entitlement-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.entitlement-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
  flex-wrap: wrap;
}

.entitlement-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.ent-asset {
  font-family: monospace;
  font-size: 0.72rem;
  color: #6b7280;
  margin-left: 0.5rem;
}

.badge {
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.badge.active {
  background: rgba(40, 167, 69, 0.15);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.2);
}

.badge.inactive {
  background: rgba(108, 117, 125, 0.15);
  color: #94A3B8;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

.badge.type {
  background: rgba(32, 180, 255, 0.15);
  color: #20B4FF;
  border: 1px solid rgba(32, 180, 255, 0.2);
  margin-right: 0.4rem;
}

@media (max-width: 768px) {
  .entitlement-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
